import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/overlays";
import { Tip } from "@/components/ui/overlays";
import { api } from "@/lib/api";

interface BillingStatus {
  enabled: boolean;
  status: string;
  current_period_end: string | null;
  price_id: string | null;
}

const LEGAL_BASE = "https://graphview.org";

/** Upgrade-to-Pro / manage-subscription control. Rendered by AccountChip only
 * when a user is signed in, hidden when billing isn't configured. Checkout +
 * portal are Stripe-HOSTED: we POST and redirect to the returned URL (no
 * Stripe.js). Before checkout we show the plan + the legal acknowledgment. */
export function BillingButton() {
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({
    queryKey: ["billing-status"],
    queryFn: () => api.request<BillingStatus>("GET", "/api/billing/status"),
    staleTime: 60_000,
  });

  if (!data || !data.enabled) return null;
  const active = data.status === "active" || data.status === "trialing";

  const go = (path: "/api/billing/checkout" | "/api/billing/portal") => {
    setBusy(true);
    void api
      .request<{ url: string }>("POST", path)
      .then(({ url }) => {
        window.location.href = url; // Stripe-hosted checkout / customer portal
      })
      .catch((e) => {
        toast.error("Could not open billing", { description: String(e) });
        setBusy(false);
      });
  };

  if (active) {
    return (
      <Tip label="Pro — manage or cancel your subscription">
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => go("/api/billing/portal")}
        >
          <BadgeCheck className="h-3.5 w-3.5 text-primary" /> Pro
        </Button>
      </Tip>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> Upgrade
        </Button>
      </DialogTrigger>
      <DialogContent title="Upgrade to GraphView Pro" className="w-[min(440px,92vw)]">
        <div className="flex flex-col gap-4">
          <div className="flex items-baseline gap-1">
            <span className="font-display text-3xl font-light text-foreground">$10</span>
            <span className="text-sm text-muted-foreground">/ month</span>
          </div>
          <ul className="grid gap-2 text-[13.5px] text-muted-foreground">
            {[
              "Frontier models (Claude, GPT, Gemini)",
              "Higher daily AI limits",
              "Cloud sync + more storage",
              "Priority routing",
            ].map((p) => (
              <li key={p} className="flex gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary shadow-[0_0_10px_#E0B15C]" />
                {p}
              </li>
            ))}
          </ul>
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">
            Billed via Stripe; cancel anytime. By continuing you agree to our{" "}
            <a
              href={`${LEGAL_BASE}/terms`}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline decoration-primary/40 underline-offset-2 hover:text-primary"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href={`${LEGAL_BASE}/privacy`}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline decoration-primary/40 underline-offset-2 hover:text-primary"
            >
              Privacy Policy
            </a>
            .
          </p>
          <Button onClick={() => go("/api/billing/checkout")} disabled={busy} className="w-full">
            Continue to checkout <span aria-hidden>→</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
